import React from 'react';

import { useIntl } from '../../../util/reactIntl';
import { profilePageParams } from '../../../util/userHelpers';
import { Avatar, NamedLink, AverageStarRating, PartnerBadgeLink } from '../../../components';

import css from './SellerCard.module.css';

const RATING_STAR_COUNT = 5;

// Matches the `focusElementId` passed to the ListingPage Modal so that
// closing the inquiry modal returns keyboard focus to this trigger.
const CONTACT_USER_LINK_ID = 'inquiryModalContactUserLink';

/**
 * Bordered seller card on the right column.
 *
 * Shows avatar + display name + the seller's average star rating
 * (and, for a verified partner, Guro's badge right after the name)
 * (computed on the fly from their public reviews — see
 * ListingPage.duck `fetchSellerReviews`), then two underlined actions:
 * "Send melding" (opens the inquiry modal) and "Se selgers profil".
 *
 * The rating row only renders once the seller has at least one rated
 * review; before that we don't show a fake score.
 *
 * @param {Object} props
 * @param {Object} props.author – denormalized user entity
 * @param {string} props.authorDisplayName
 * @param {number} props.rating – average rating (0 when no reviews)
 * @param {number} props.reviewCount – number of rated reviews
 * @param {Function} props.onContactUser – opens the inquiry modal
 * @returns {JSX.Element|null}
 */
const SellerCard = props => {
  const { author, authorDisplayName, rating = 0, reviewCount = 0, onContactUser } = props;
  const intl = useIntl();

  if (!author?.id?.uuid) return null;

  const hasRating = reviewCount > 0;

  return (
    <section className={css.root}>
      <div className={css.profile}>
        <Avatar className={css.avatar} user={author} />
        <div className={css.profileText}>
          <p className={css.name}>{authorDisplayName}</p>
          {hasRating ? (
            <div
              className={css.ratingRow}
              role="img"
              aria-label={intl.formatMessage(
                { id: 'ListingPage.sellerRatingAccessibility' },
                {
                  rating: rating.toFixed(1),
                  outOf: RATING_STAR_COUNT,
                  count: reviewCount,
                }
              )}
            >
              <span aria-hidden="true">
                <AverageStarRating rating={rating} />
              </span>
              <p className={css.ratingText} aria-hidden="true">
                {intl.formatMessage(
                  { id: 'ListingPage.sellerRating' },
                  {
                    rating: rating.toFixed(1),
                    count: reviewCount,
                  }
                )}
              </p>
            </div>
          ) : null}
        </div>
        {/* A verified partner: Guro's badge right after the name, the size
            of the avatar, leading to the Butikker page (Guro, 9 October). */}
        <PartnerBadgeLink user={author} className={css.partnerBadge} />
      </div>

      <div className={css.actions}>
        <button
          type="button"
          id={CONTACT_USER_LINK_ID}
          className={css.actionLink}
          onClick={onContactUser}
        >
          {intl.formatMessage({ id: 'ListingPage.sellerCardSendMessage' })}
        </button>
        <span className={css.actionDot} aria-hidden="true">
          ·
        </span>
        <NamedLink className={css.actionLink} name="ProfilePage" params={profilePageParams(author)}>
          {intl.formatMessage({ id: 'ListingPage.sellerCardViewProfile' })}
        </NamedLink>
      </div>
    </section>
  );
};

export default SellerCard;
